/**
 * 통합 알림 시스템 Composable
 *
 * 프로젝트 전체에서 사용하는 알림(notification, toast, alert)을 통합 관리
 */

import { ref, type Ref } from 'vue'

/**
 * 알림 타입 정의
 */
export type NotificationType = 'success' | 'error' | 'warning' | 'info'

/**
 * 알림 위치 정의
 */
export type NotificationPosition =
  | 'top'
  | 'top-right'
  | 'top-left'
  | 'bottom'
  | 'bottom-right'
  | 'bottom-left'

/**
 * 알림 옵션
 */
export interface NotificationOptions {
  title?: string
  message: string
  type?: NotificationType
  duration?: number // milliseconds, 0 = 영구
  closable?: boolean
  position?: NotificationPosition
  icon?: string
  action?: {
    label: string
    handler: () => void
  }
}

/**
 * 알림 아이템
 */
export interface NotificationItem extends Required<Omit<NotificationOptions, 'action'>> {
  id: string
  show: boolean
  action?: NotificationOptions['action']
}

/**
 * 알림 시스템 상태
 */
interface NotificationState {
  notifications: Ref<NotificationItem[]>
  defaultOptions: NotificationOptions
  timeouts: Map<string, number>
}

// 전역 알림 상태 (앱 전체에서 공유)
const globalState: NotificationState = {
  notifications: ref<NotificationItem[]>([]),
  defaultOptions: {
    type: 'info',
    duration: 3000,
    closable: true,
    position: 'top-right',
    message: '',
  },
  timeouts: new Map<string, number>(),
}

export function useUnifiedNotification() {
  const notifications = globalState.notifications
  const timeouts = globalState.timeouts

  /**
   * 알림 ID 생성
   */
  const generateId = (): string => {
    return `notification-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  /**
   * 타입별 기본 아이콘 가져오기
   */
  const getDefaultIcon = (type: NotificationType): string => {
    switch (type) {
      case 'success':
        return '✅'
      case 'error':
        return '❌'
      case 'warning':
        return '⚠️'
      case 'info':
        return 'ℹ️'
      default:
        return ''
    }
  }

  /**
   * 알림 표시
   */
  const show = (options: NotificationOptions | string): string => {
    // 문자열만 전달된 경우 간단한 info 메시지로 처리
    const opts = typeof options === 'string' ? { message: options } : options

    const notification: NotificationItem = {
      id: generateId(),
      show: true,
      title: opts.title || '',
      message: opts.message,
      type: opts.type || globalState.defaultOptions.type || 'info',
      duration:
        opts.duration !== undefined ? opts.duration : globalState.defaultOptions.duration || 3000,
      closable:
        opts.closable !== undefined ? opts.closable : globalState.defaultOptions.closable || true,
      position: opts.position || globalState.defaultOptions.position || 'top-right',
      icon: opts.icon || getDefaultIcon(opts.type || 'info'),
      action: opts.action,
    }

    // 알림 추가
    notifications.value.push(notification)

    // 자동 숨김 설정 (duration이 0이 아닌 경우)
    if (notification.duration > 0) {
      // 클라이언트 사이드에서만 타이머 실행
      if (typeof window !== 'undefined') {
        const timeout = window.setTimeout(() => {
          hide(notification.id)
        }, notification.duration)
        timeouts.set(notification.id, timeout)
      }
    }

    return notification.id
  }

  /**
   * 성공 알림
   */
  const success = (
    message: string,
    title?: string,
    options?: Partial<NotificationOptions>
  ): string => {
    return show({
      ...options,
      message,
      title,
      type: 'success',
    })
  }

  /**
   * 에러 알림
   */
  const error = (
    message: string,
    title?: string,
    options?: Partial<NotificationOptions>
  ): string => {
    return show({
      ...options,
      message,
      title,
      type: 'error',
      duration: options?.duration !== undefined ? options.duration : 5000, // 에러는 기본 5초
    })
  }

  /**
   * 경고 알림
   */
  const warning = (
    message: string,
    title?: string,
    options?: Partial<NotificationOptions>
  ): string => {
    return show({
      ...options,
      message,
      title,
      type: 'warning',
    })
  }

  /**
   * 정보 알림
   */
  const info = (
    message: string,
    title?: string,
    options?: Partial<NotificationOptions>
  ): string => {
    return show({
      ...options,
      message,
      title,
      type: 'info',
    })
  }

  /**
   * 알림 숨기기
   */
  const hide = (id: string) => {
    const index = notifications.value.findIndex((n) => n.id === id)
    if (index !== -1) {
      notifications.value[index].show = false

      // 애니메이션 완료 후 제거
      setTimeout(() => {
        const idx = notifications.value.findIndex((n) => n.id === id)
        if (idx !== -1) {
          notifications.value.splice(idx, 1)
        }
      }, 300)

      // 타임아웃 정리
      const timeout = timeouts.get(id)
      if (timeout) {
        clearTimeout(timeout)
        timeouts.delete(id)
      }
    }
  }

  /**
   * 모든 알림 숨기기
   */
  const hideAll = () => {
    // 현재 존재하는 알림 ID들을 저장 (나중에 추가된 알림은 제거하지 않음)
    const idsToRemove = new Set(notifications.value.map((n) => n.id))

    notifications.value.forEach((n) => {
      n.show = false
    })

    // 제거 대상 알림들의 타임아웃만 정리
    idsToRemove.forEach((id) => {
      const timeout = timeouts.get(id)
      if (timeout) {
        clearTimeout(timeout)
        timeouts.delete(id)
      }
    })

    // 애니메이션 완료 후 해당 알림들만 제거 (이후 추가된 알림은 유지)
    setTimeout(() => {
      notifications.value = notifications.value.filter((n) => !idsToRemove.has(n.id))
    }, 300)
  }

  /**
   * 특정 타입의 알림만 숨기기
   */
  const hideByType = (type: NotificationType) => {
    notifications.value.filter((n) => n.type === type).forEach((n) => hide(n.id))
  }

  /**
   * 확인 대화상자 (Promise 기반)
   */
  const confirm = (message: string, title: string = '확인'): Promise<boolean> => {
    return new Promise((resolve) => {
      const id = show({
        title,
        message,
        type: 'warning',
        duration: 0, // 영구 표시
        closable: false,
        action: {
          label: '확인',
          handler: () => {
            hide(id)
            resolve(true)
          },
        },
      })

      // 취소 버튼 추가 (추후 구현 가능)
      // 현재는 X 버튼으로 닫으면 false 반환
      const notification = notifications.value.find((n) => n.id === id)
      if (notification) {
        notification.closable = true
        // closable 클릭 시 false 반환하도록 처리 필요
      }
    })
  }

  /**
   * 로딩 알림 (수동으로 닫아야 함)
   */
  const loading = (message: string = '처리 중...'): string => {
    return show({
      message,
      type: 'info',
      duration: 0,
      closable: false,
      icon: '⏳',
    })
  }

  /**
   * 클립보드 복사 성공 알림
   */
  const copied = (message: string = '클립보드에 복사되었습니다') => {
    return success(message)
  }

  /**
   * 저장 성공 알림
   */
  const saved = (message: string = '저장되었습니다') => {
    return success(message)
  }

  /**
   * 삭제 성공 알림
   */
  const deleted = (message: string = '삭제되었습니다') => {
    return success(message)
  }

  /**
   * 네트워크 에러 알림
   */
  const networkError = (message: string = '네트워크 연결을 확인해주세요') => {
    return error(message, '연결 오류')
  }

  /**
   * 권한 에러 알림
   */
  const permissionError = (message: string = '권한이 없습니다') => {
    return error(message, '권한 오류')
  }

  /**
   * 유효성 검사 에러 알림
   */
  const validationError = (message: string) => {
    return error(message, '입력 오류')
  }

  /**
   * 기본 옵션 설정
   */
  const setDefaultOptions = (options: Partial<NotificationOptions>) => {
    Object.assign(globalState.defaultOptions, options)
  }

  /**
   * cleanup 함수 (컴포넌트 언마운트 시 호출)
   */
  const cleanup = () => {
    timeouts.forEach((timeout) => clearTimeout(timeout))
    timeouts.clear()
  }

  return {
    // 상태
    notifications,

    // 기본 메서드
    show,
    hide,
    hideAll,
    hideByType,

    // 타입별 메서드
    success,
    error,
    warning,
    info,

    // 특수 메서드
    confirm,
    loading,

    // 자주 사용하는 케이스
    copied,
    saved,
    deleted,
    networkError,
    permissionError,
    validationError,

    // 설정
    setDefaultOptions,

    // 정리
    cleanup,
  }
}
